import React, { useEffect, useRef, useState } from 'react';
import { Button, Result, Spin } from 'antd';
import '../../components/MobilePdfPreview.css';

const MobilePdfPreview = ({ dataUrl, openPdf }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [pdfDocument, setPdfDocument] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    let loadingTask;
    setPdfDocument(null);
    setPageNumber(1);
    setZoom(1);
    setError('');
    setLoading(true);

    const load = async () => {
      try {
        const library = await import('pdfjs-dist/webpack');
        if (!active) return;
        const pdfjs = library.getDocument ? library : library.default;
        const base64 = dataUrl.split(',')[1];
        if (!base64) throw new Error('ไม่พบข้อมูล PDF');
        const bytes = Uint8Array.from(atob(base64), character => character.charCodeAt(0));
        loadingTask = pdfjs.getDocument({ data: bytes });
        const document = await loadingTask.promise;
        if (active) setPdfDocument(document);
        else document.destroy();
      } catch (loadError) {
        if (active) {
          console.error('Mobile PDF load error:', loadError);
          setError('ไม่สามารถแสดงตัวอย่าง PDF บนอุปกรณ์นี้ได้');
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
      if (loadingTask) loadingTask.destroy();
    };
  }, [dataUrl]);

  useEffect(() => {
    if (!pdfDocument) return undefined;
    let active = true;
    let renderTask;
    setLoading(true);
    setError('');

    const render = async () => {
      try {
        const page = await pdfDocument.getPage(pageNumber);
        if (!active) return;
        const container = containerRef.current;
        const canvas = canvasRef.current;
        if (!container || !canvas) return;
        const original = page.getViewport({ scale: 1 });
        const availableWidth = Math.max(container.clientWidth - 24, 240);
        const viewport = page.getViewport({ scale: (availableWidth / original.width) * zoom });
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        const context = canvas.getContext('2d');
        canvas.width = Math.floor(viewport.width * pixelRatio);
        canvas.height = Math.floor(viewport.height * pixelRatio);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        renderTask = page.render({
          canvasContext: context,
          viewport,
          transform: pixelRatio === 1 ? null : [pixelRatio, 0, 0, pixelRatio, 0, 0]
        });
        await renderTask.promise;
        if (active) setLoading(false);
      } catch (renderError) {
        if (active && renderError.name !== 'RenderingCancelledException') {
          console.error('Mobile PDF render error:', renderError);
          setError('ไม่สามารถแสดงหน้านี้ได้');
          setLoading(false);
        }
      }
    };

    render();
    return () => {
      active = false;
      if (renderTask) renderTask.cancel();
    };
  }, [pdfDocument, pageNumber, zoom]);

  if (error) {
    return <Result status="warning" title={error}
      extra={<Button type="primary" onClick={openPdf}>เปิดไฟล์ PDF</Button>} />;
  }

  return <div className="receipt-mobile-pdf">
    <div className="receipt-mobile-pdf-toolbar">
      <Button size="small" disabled={!pdfDocument || pageNumber <= 1}
        onClick={() => setPageNumber(page => page - 1)}>ก่อนหน้า</Button>
      <span aria-live="polite">{pdfDocument ? `${pageNumber} / ${pdfDocument.numPages}` : 'กำลังโหลด'}</span>
      <Button size="small" disabled={!pdfDocument || pageNumber >= pdfDocument.numPages}
        onClick={() => setPageNumber(page => page + 1)}>ถัดไป</Button>
      <Button size="small" disabled={!pdfDocument || zoom <= 0.8}
        onClick={() => setZoom(value => Math.max(0.8, +(value - 0.2).toFixed(1)))} aria-label="ย่อเอกสาร">−</Button>
      <Button size="small" disabled={!pdfDocument || zoom >= 2}
        onClick={() => setZoom(value => Math.min(2, +(value + 0.2).toFixed(1)))} aria-label="ขยายเอกสาร">+</Button>
    </div>
    <div ref={containerRef} className="receipt-mobile-pdf-scroll">
      {loading && <div className="receipt-mobile-pdf-loading"><Spin /> กำลังแสดง PDF...</div>}
      <canvas ref={canvasRef} className="receipt-mobile-pdf-canvas" aria-label={`หน้า ${pageNumber} ของเอกสาร`} />
    </div>
  </div>;
};

export default MobilePdfPreview;
